import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/content";
import { Reveal, Overline } from "@/components/site/Reveal";

export default function Gallery() {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    (e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length); },
    []
  );
  const next = useCallback(
    (e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % GALLERY.length); },
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <section className="relative bg-white py-24 md:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline className="text-sun">Momentos reais</Overline>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-brand-navy sm:text-5xl">
            Um mundo inteiro esperando por você
          </h2>
        </Reveal>

        <div className="mt-14 columns-2 gap-4 md:columns-3 [&>*]:mb-4">
          {GALLERY.map((item, i) => (
            <motion.button
              key={i}
              onClick={() => setIndex(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="group relative block w-full overflow-hidden rounded-2xl"
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={item.src}
                alt={item.cap}
                className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <span className="absolute bottom-3 left-3 right-3 text-left text-xs font-medium text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                {item.cap}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-ocean-deep/95 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="lightbox"
          >
            <button onClick={close} className="absolute right-5 top-5 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" data-testid="lightbox-close">
              <X className="h-5 w-5" />
            </button>
            <button onClick={prev} className="absolute left-3 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 sm:left-8" data-testid="lightbox-prev">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button onClick={next} className="absolute right-3 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 sm:right-8" data-testid="lightbox-next">
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.figure
              key={index}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[85vh] max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={GALLERY[index].src} alt={GALLERY[index].cap} className="max-h-[80vh] w-auto rounded-2xl object-contain" />
              <figcaption className="mt-3 text-center text-sm text-white/80">{GALLERY[index].cap}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
